/**
 * brand-completeness — how much of a brand identity is actually filled in.
 *
 * FEAT-087 / S-375. The handoff wants the Brand Identity index to answer
 * "why do this publication's images come back generic?" before anyone has to
 * ask it. The answer is usually that the identity is mostly empty: the Art
 * Director prompts from these fields, so a blank one produces stock-looking
 * output and no error anywhere.
 *
 * The ten fields below are the ones the pipeline reads. Counting them is a
 * blunt measure and deliberately so — it is a prompt to go look, not a score.
 */

export interface BrandIdentityLike {
  primary_palette?: Record<string, unknown> | null;
  secondary_palette?: Record<string, unknown> | null;
  typography?: Record<string, unknown> | null;
  color_rules?: unknown[] | null;
  default_image_style?: string | null;
  allowed_image_styles?: unknown[] | null;
  image_treatment?: string | null;
  photography_style?: string | null;
  logo_file_id?: number | string | null;
  source_markdown?: string | null;
}

/** A field counts as filled when it would actually reach a prompt. */
function filled(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return true;
}

export const BRAND_FIELDS: ReadonlyArray<{
  key: keyof BrandIdentityLike;
  label: string;
}> = [
  { key: "primary_palette", label: "Primary palette" },
  { key: "secondary_palette", label: "Secondary palette" },
  { key: "typography", label: "Typography" },
  { key: "color_rules", label: "Colour rules" },
  { key: "default_image_style", label: "Default image style" },
  { key: "allowed_image_styles", label: "Allowed image styles" },
  { key: "image_treatment", label: "Image treatment" },
  { key: "photography_style", label: "Photography style" },
  { key: "logo_file_id", label: "Logo" },
  { key: "source_markdown", label: "brand-identity.md" },
];

export interface BrandCompleteness {
  filled: number;
  total: number;
  /** Below this the identity is thin enough to explain generic output. */
  thin: boolean;
  missing: string[];
}

/** The threshold the handoff draws amber at: 4 of 10 reads as a problem. */
export const BRAND_THIN_BELOW = 6;

export function brandCompleteness(
  identity: BrandIdentityLike | null | undefined
): BrandCompleteness {
  const total = BRAND_FIELDS.length;
  if (!identity) {
    return { filled: 0, total, thin: true, missing: BRAND_FIELDS.map((f) => f.label) };
  }
  const missing: string[] = [];
  let count = 0;
  for (const field of BRAND_FIELDS) {
    if (filled(identity[field.key])) count += 1;
    else missing.push(field.label);
  }
  return { filled: count, total, thin: count < BRAND_THIN_BELOW, missing };
}

/**
 * The palette's colours in role order, for the index's swatches. Only strings
 * that look like colours are returned — the palette is free-form jsonb, and
 * rendering an arbitrary value as a swatch would paint nothing and say
 * nothing.
 */
export function paletteSwatches(
  palette: Record<string, unknown> | null | undefined
): Array<{ role: string; color: string }> {
  if (!palette) return [];
  const ROLE_ORDER = ["background", "surface", "text", "heading", "accent", "link"];
  const out: Array<{ role: string; color: string }> = [];
  const isColor = (v: unknown): v is string =>
    typeof v === "string" && /^#[0-9a-f]{3,8}$/i.test(v.trim());
  for (const role of ROLE_ORDER) {
    const v = palette[role];
    if (isColor(v)) out.push({ role, color: v.trim() });
  }
  // Any further colour roles the palette carries, in their own order.
  for (const [role, v] of Object.entries(palette)) {
    if (ROLE_ORDER.includes(role)) continue;
    if (isColor(v)) out.push({ role, color: v.trim() });
  }
  return out;
}
